import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import '../../pages-styles/project-consultancy.css';
import { usePageMeta } from '../../hooks/usePageMeta';

export default function Development() {
  usePageMeta(
    'Web, Application & System Development - Magbity Agency Ltd',
    'Custom web development, mobile apps (iOS/Android), full-stack development with React, Flutter, Django, Laravel, Node.js, and modern databases.'
  );

  useEffect(() => {
    const el = document.querySelector('.dev-3d-card');
    if (el) el.classList.add('enter');
  }, []);

  return (
    <div className="project-consultancy">
      <section className="consultancy-hero-banner dev-hero">
        <div className="consultancy-hero-overlay"></div>
        <div className="consultancy-hero-content">
          <div className="consultancy-hero-text">
            <h1>Web, Application & System Development</h1>
            <p>
              Custom-built digital solutions tailored to your business needs. From responsive web applications 
              to native mobile apps, we leverage modern frameworks and technologies to deliver scalable, secure, 
              and user-centric solutions that drive business value.
            </p>
          </div>

          <div className="dev-illustration">
            <div className="dev-3d-scene">
              <div className="dev-3d-card">
                <div className="face front">
                  <svg width="220" height="220" viewBox="0 0 220 220" xmlns="http://www.w3.org/2000/svg">
                    <defs>
                      <linearGradient id="devGrad" x1="0" x2="1">
                        <stop offset="0%" stopColor="#16c784" />
                        <stop offset="100%" stopColor="#00d4ff" />
                      </linearGradient>
                    </defs>
                    <rect x="10" y="10" width="200" height="200" rx="16" fill="url(#devGrad)" opacity="0.12" />
                    <g transform="translate(25,35) scale(0.95)">
                      <rect x="15" y="5" width="50" height="50" rx="4" fill="none" stroke="#fff" strokeWidth="2.5" opacity="0.15" />
                      <circle cx="30" cy="30" r="3" fill="#fff" opacity="0.12" />
                      <circle cx="50" cy="30" r="3" fill="#fff" opacity="0.12" />
                      <rect x="35" y="65" width="30" height="8" rx="2" fill="#fff" opacity="0.1" />
                      <rect x="35" y="80" width="40" height="8" rx="2" fill="#fff" opacity="0.1" />
                      <rect x="35" y="95" width="25" height="8" rx="2" fill="#fff" opacity="0.1" />
                    </g>
                  </svg>
                  <div className="card-label">Custom Solutions</div>
                </div>
                <div className="face back">
                  <h4>Development Excellence</h4>
                  <p>Web, mobile, and full-stack development with cutting-edge technologies.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="consultancy-services dev-details">
        <h2>Development Services</h2>
        <div className="services-list">
          <div className="service-item left">
            <div className="service-content">
              <h3>Web Application Development</h3>
              <p>
                Build scalable, responsive, and interactive web applications using modern frontend frameworks 
                and robust backend systems. We create user-centric solutions optimized for performance and accessibility.
              </p>
              <ul className="service-details-list">
                <li><strong>Frontend:</strong> React.js, Vue.js, Angular, Next.js</li>
                <li><strong>Backend:</strong> Node.js/Express.js, Django, Laravel, ASP.NET</li>
                <li><strong>Databases:</strong> PostgreSQL, MongoDB, MySQL, Redis</li>
                <li>Progressive Web Apps (PWA) and responsive design</li>
                <li>SEO optimization and performance tuning</li>
              </ul>
            </div>
            <div className="service-icon-wrapper">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="#16c784" xmlns="http://www.w3.org/2000/svg">
                <path d="M20 2H4c-1.1 0-2 .9-2 2v18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 18H4V4h16v16zm-6-9h-4v4h4v-4zm-6-4h4v4h-4V7zm6 0h4v4h-4V7zM8 17h4v4H8v-4zm6 0h4v4h-4v-4z" opacity=".2"/>
                <path d="M20 2H4c-1.1 0-2 .9-2 2v18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 18H4V4h16v16zm-6-9h-4v4h4v-4zm-6-4h4v4h-4V7zm6 0h4v4h-4V7zM8 17h4v4H8v-4zm6 0h4v4h-4v-4z"/>
              </svg>
            </div>
          </div>

          <div className="service-item right">
            <div className="service-icon-wrapper">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="#16c784" xmlns="http://www.w3.org/2000/svg">
                <path d="M17 2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V7l-5-5zm-5 16c-1.66 0-3-1.34-3-3s1.34-3 3-3 3 1.34 3 3-1.34 3-3 3zm3-10H5V5h10v3z" opacity=".2"/>
                <path d="M17 2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V7l-5-5zm-5 16c-1.66 0-3-1.34-3-3s1.34-3 3-3 3 1.34 3 3-1.34 3-3 3zm3-10H5V5h10v3z"/>
              </svg>
            </div>
            <div className="service-content">
              <h3>Mobile App Development</h3>
              <p>
                Native and cross-platform mobile applications for iOS and Android with intuitive UI/UX design, 
                offline capabilities, and seamless backend integration for maximum user engagement.
              </p>
              <ul className="service-details-list">
                <li><strong>Native:</strong> Swift (iOS), Kotlin (Android)</li>
                <li><strong>Cross-Platform:</strong> React Native, Flutter, Xamarin</li>
                <li><strong>Features:</strong> Push notifications, biometric auth, offline sync</li>
                <li>App Store & Google Play deployment</li>
                <li>Performance optimization and battery efficiency</li>
              </ul>
            </div>
          </div>

          <div className="service-item left">
            <div className="service-content">
              <h3>Full-Stack System Development</h3>
              <p>
                End-to-end system development combining frontend interfaces, backend APIs, database design, 
                and cloud infrastructure for enterprise-grade solutions that scale with your business.
              </p>
              <ul className="service-details-list">
                <li><strong>Frontend Stack:</strong> React, Vue, TypeScript, Tailwind CSS</li>
                <li><strong>Backend Stack:</strong> Node.js, Django, Laravel, Java</li>
                <li><strong>Databases:</strong> PostgreSQL, MongoDB, Firebase, DynamoDB</li>
                <li>Microservices architecture and API design</li>
                <li>Cloud deployment (AWS, Azure, Google Cloud, Firebase)</li>
              </ul>
            </div>
            <div className="service-icon-wrapper">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="#16c784" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zM7 12.5c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zm5-2c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm0 6c-2.7 0-5.1-1.23-6.71-3.15.17-.67.65-1.27 1.3-1.57 1.06-.6 2.28-.99 3.41-.99s2.35.39 3.41.99c.65.3 1.13.9 1.3 1.57-1.61 1.92-4.01 3.15-6.71 3.15zm5.5-4.5c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5z" opacity=".2"/>
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zM7 12.5c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zm5-2c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm0 6c-2.7 0-5.1-1.23-6.71-3.15.17-.67.65-1.27 1.3-1.57 1.06-.6 2.28-.99 3.41-.99s2.35.39 3.41.99c.65.3 1.13.9 1.3 1.57-1.61 1.92-4.01 3.15-6.71 3.15zm5.5-4.5c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5z"/>
              </svg>
            </div>
          </div>

          <div className="service-item right">
            <div className="service-icon-wrapper">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="#16c784" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm3.6 8.3h-2.68V7.69h2.68V10.3zm0 2.4h-2.68v2.61h2.68v-2.61zM9.4 10.3H6.72V7.69H9.4v2.61zm0 2.4H6.72v2.61H9.4v-2.61zm-3.4 5.01c-.77 0-1.4-.63-1.4-1.4s.63-1.4 1.4-1.4 1.4.63 1.4 1.4-.63 1.4-1.4 1.4z" opacity=".2"/>
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm3.6 8.3h-2.68V7.69h2.68V10.3zm0 2.4h-2.68v2.61h2.68v-2.61zM9.4 10.3H6.72V7.69H9.4v2.61zm0 2.4H6.72v2.61H9.4v-2.61zm-3.4 5.01c-.77 0-1.4-.63-1.4-1.4s.63-1.4 1.4-1.4 1.4.63 1.4 1.4-.63 1.4-1.4 1.4z"/>
              </svg>
            </div>
            <div className="service-content">
              <h3>UI/UX Design & Frontend Development</h3>
              <p>
                Intuitive and aesthetically pleasing user interfaces that drive engagement and user satisfaction. 
                We combine design excellence with development best practices for optimal user experiences.
              </p>
              <ul className="service-details-list">
                <li>Responsive design for all devices and screen sizes</li>
                <li>Accessibility compliance (WCAG, ARIA standards)</li>
                <li>Performance optimization and core web vitals</li>
                <li>Interactive components and animations</li>
                <li>Design systems and component libraries</li>
              </ul>
            </div>
          </div>

          <div className="service-item left">
            <div className="service-content">
              <h3>Database Design & Data Management</h3>
              <p>
                Robust database architecture designed for scalability, security, and performance. We optimize 
                data models, implement caching strategies, and ensure efficient data operations.
              </p>
              <ul className="service-details-list">
                <li><strong>Relational:</strong> PostgreSQL, MySQL, SQL Server</li>
                <li><strong>NoSQL:</strong> MongoDB, Firebase, Cassandra, DynamoDB</li>
                <li><strong>Caching:</strong> Redis, Memcached</li>
                <li>Data migration and ETL processes</li>
                <li>Backup, disaster recovery, and data security</li>
              </ul>
            </div>
            <div className="service-icon-wrapper">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="#16c784" xmlns="http://www.w3.org/2000/svg">
                <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V5h14v14zM7 7h10v2H7zm0 4h10v2H7zm0 4h7v2H7z" opacity=".2"/>
                <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V5h14v14zM7 7h10v2H7zm0 4h10v2H7zm0 4h7v2H7z"/>
              </svg>
            </div>
          </div>

          <div className="service-item right">
            <div className="service-icon-wrapper">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="#16c784" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" opacity=".2"/>
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
              </svg>
            </div>
            <div className="service-content">
              <h3>API Development & Integration</h3>
              <p>
                RESTful and GraphQL API development with robust error handling, authentication, rate limiting, 
                and comprehensive documentation for seamless third-party integrations.
              </p>
              <ul className="service-details-list">
                <li>RESTful API design and GraphQL implementation</li>
                <li>Authentication & authorization (JWT, OAuth2)</li>
                <li>API documentation (Swagger/OpenAPI, Postman)</li>
                <li>Third-party service integration (payment, SMS, email)</li>
                <li>Webhook management and event-driven architecture</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="technology-stack">
        <h2>Technology Stack & Expertise</h2>
        <div className="tech-grid">
          <div className="tech-category">
            <h4>Frontend Technologies</h4>
            <div className="tech-tags">
              <span className="tech-tag">React.js</span>
              <span className="tech-tag">Vue.js</span>
              <span className="tech-tag">Angular</span>
              <span className="tech-tag">Next.js</span>
              <span className="tech-tag">TypeScript</span>
              <span className="tech-tag">Tailwind CSS</span>
              <span className="tech-tag">Bootstrap</span>
            </div>
          </div>

          <div className="tech-category">
            <h4>Mobile Development</h4>
            <div className="tech-tags">
              <span className="tech-tag">React Native</span>
              <span className="tech-tag">Flutter</span>
              <span className="tech-tag">Swift</span>
              <span className="tech-tag">Kotlin</span>
              <span className="tech-tag">Xamarin</span>
              <span className="tech-tag">Expo</span>
            </div>
          </div>

          <div className="tech-category">
            <h4>Backend Technologies</h4>
            <div className="tech-tags">
              <span className="tech-tag">Node.js/Express</span>
              <span className="tech-tag">Django</span>
              <span className="tech-tag">Laravel</span>
              <span className="tech-tag">Java/Spring</span>
              <span className="tech-tag">Python</span>
              <span className="tech-tag">PHP</span>
              <span className="tech-tag">Go</span>
            </div>
          </div>

          <div className="tech-category">
            <h4>Databases</h4>
            <div className="tech-tags">
              <span className="tech-tag">PostgreSQL</span>
              <span className="tech-tag">MongoDB</span>
              <span className="tech-tag">MySQL</span>
              <span className="tech-tag">Firebase</span>
              <span className="tech-tag">Redis</span>
              <span className="tech-tag">Elasticsearch</span>
              <span className="tech-tag">DynamoDB</span>
            </div>
          </div>

          <div className="tech-category">
            <h4>Cloud & DevOps</h4>
            <div className="tech-tags">
              <span className="tech-tag">AWS</span>
              <span className="tech-tag">Google Cloud</span>
              <span className="tech-tag">Azure</span>
              <span className="tech-tag">Docker</span>
              <span className="tech-tag">Kubernetes</span>
              <span className="tech-tag">CI/CD</span>
              <span className="tech-tag">Heroku</span>
            </div>
          </div>

          <div className="tech-category">
            <h4>Tools & Practices</h4>
            <div className="tech-tags">
              <span className="tech-tag">Git</span>
              <span className="tech-tag">Testing</span>
              <span className="tech-tag">API Documentation</span>
              <span className="tech-tag">Agile</span>
              <span className="tech-tag">Code Review</span>
              <span className="tech-tag">Performance Monitoring</span>
            </div>
          </div>
        </div>
      </section>

      <section className="dev-process">
        <h2>Development Process</h2>
        <div className="process-steps">
          <div className="step">
            <div className="step-number">1</div>
            <h4>Requirements & Design</h4>
            <p>Understand business goals and design architecture</p>
          </div>
          <div className="step">
            <div className="step-number">2</div>
            <h4>Prototyping</h4>
            <p>Create wireframes and interactive prototypes</p>
          </div>
          <div className="step">
            <div className="step-number">3</div>
            <h4>Development</h4>
            <p>Build features using agile methodologies</p>
          </div>
          <div className="step">
            <div className="step-number">4</div>
            <h4>Testing</h4>
            <p>Quality assurance and user acceptance testing</p>
          </div>
          <div className="step">
            <div className="step-number">5</div>
            <h4>Deployment</h4>
            <p>Launch to production with zero downtime</p>
          </div>
          <div className="step">
            <div className="step-number">6</div>
            <h4>Support</h4>
            <p>Maintenance, monitoring, and continuous improvement</p>
          </div>
        </div>
      </section>

      <section className="cta-section">
        <h2>Ready to Build Your Custom Solution?</h2>
        <p>Let's discuss your project requirements and create a technology roadmap tailored to your vision.</p>
        <Link to="/contact" className="cta-button">Start Your Development Project</Link>
      </section>
    </div>
  );
}
